import { FC } from 'react';
import { Phone, Regular, PrimaryButton, ZazumeModal, Shadows, Flex, ZZMForm, Semibold } from '@zazume/zzm-base';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { PhoneNumberInput, getPhoneNumberSchema } from './schema';
import { useI18n } from '#/hooks/useI18n';
import { useUpdate } from '#/lib/reactQuery/hooks/useUpdate';
import { API } from '#/lib/network/API';
import { ContactKeys } from '#/hooks/api/contact/Contact.keys';
import { useSession } from '#/hooks/session/useSession';
import { useAsyncMethod } from '#/hooks/useAsyncMethod';
import { useNotifications } from '#/hooks';
import { PhoneVO } from '@zazume/zzm-utils';

const Card = styled.div<any>(({ theme }) => css`
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.White};
  border-radius: ${theme.radius.lg};
  box-shadow: ${Shadows.SHADOW_3};
  padding: ${theme.spacing.md};
`);

const Container = styled.div(({ theme }) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.s10};
  padding: ${theme.spacing.s6} ${theme.spacing.s4};
`);

const FormContainer = styled.div(() => css`
  width: 100%;
`);

const Button = styled(PrimaryButton)(() => css`
  width: auto;
  white-space: nowrap;
  `);

interface CallUsModalProps {
  onClose: () => void;
}

export const CallUsModal: FC<CallUsModalProps> = ({ onClose }) => {
  const { t } = useI18n();
  const { user } = useSession();
  const { call } = useAsyncMethod();
  const { showNotification } = useNotifications();

  const { mutate: requestCall } = useUpdate(
    ContactKeys.requestPropertyManagementCallByUser(user._id),
    API.contact.propertyManagementCallHelp);


  const savePhoneNumber = (formData: PhoneNumberInput) => call(async () => {
    await requestCall({ contactPhone: formData.contactPhone, queryReason: formData.queryReason });
    onClose();
    showNotification(t('helpHub.callUsModal.success'));
  });

  const schema = getPhoneNumberSchema(t);
  const defaultValues = {
    contactPhone: PhoneVO.from(user.phoneNumber).international || '',
    queryReason: ''
  };

  return (
    <ZazumeModal title={t('helpHub.callUsModal.title')} onClose={onClose}>
      <Container>
        <Card>
          <Flex direction="column" gap="s6">
            <Semibold variant="M">
              {t('helpHub.callUsModal.subtitle')}
            </Semibold>
            <Flex gap="s6" align="center">
              <Phone size={40} color="Gray600"/>
              <Flex direction="column">
                <Regular variant="M">{t('helpHub.callUsModal.callUs')}</Regular>
                <Regular variant="S" color="Gray500">{t('helpHub.callUsModal.callHours')}</Regular>
              </Flex>
            </Flex>
          </Flex>
        </Card>
        <Card>
          <Flex gap="s6" justify="start">
            <Phone size={40} color="Gray600"/>
            <Flex direction="column" gap="s6">
              <Flex direction="column">
                <Regular variant="M">{t('helpHub.callUsModal.weCallYou')}</Regular>
                <Regular variant="S" color="Gray500">{t('helpHub.callUsModal.callbackHours')}</Regular>
              </Flex>
              <FormContainer>
                <ZZMForm.Form onSubmit={savePhoneNumber} validation={schema} defaultValues={defaultValues}>
                  <Flex direction="column" gap="s4">
                    <ZZMForm.Textarea
                      name="queryReason"
                      label={t('helpHub.callUsModal.queryReasonLabel')}
                      placeholder={t('helpHub.callUsModal.queryReasonPlaceholder')}
                    />
                    <Flex gap="s4" align="center">
                      <ZZMForm.Input
                        name="contactPhone"
                        placeholder={t('helpHub.callUsModal.phoneInputPlaceholder')}
                        type="tel"
                        responsive={false}
                      />
                      <Button type="submit">
                        {t('helpHub.callUsModal.buttonText')}
                      </Button>
                    </Flex>
                  </Flex>
                </ZZMForm.Form>
              </FormContainer>
            </Flex>
          </Flex>
        </Card>
      </Container>
    </ZazumeModal>
  );
};
